import React, { useState, useEffect } from 'react';
import { 
  PiggyBank, Plus, Search, Filter, CheckCircle2, XCircle, 
  Clock, DollarSign, Users, TrendingUp, Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { useBonuses } from '../../../hooks/useDatabase';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus, getBonusTypeInfo } from '../../../lib/payrollCalculations';
import { formatDate } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

export default function BonusManagement() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [bonuses, setBonuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalDeptFilter, setModalDeptFilter] = useState('all');
  const [formData, setFormData] = useState({
    userId: '',
    type: 'performance',
    amount: '',
    reason: ''
  });

  const { createBonus, approveBonus, rejectBonus } = useBonuses();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [bonusesData, usersData] = await Promise.all([
        db.bonuses.toArray(),
        db.users.toArray()
      ]);
      setBonuses(bonusesData);
      setUsers(usersData.filter(u => u.status === 'active' && u.role !== 'admin'));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBonuses = bonuses.filter(bonus => {
    const employee = users.find(u => u.id === bonus.userId);
    const matchesSearch = 
      employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bonus.reason?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || bonus.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingBonuses = bonuses.filter(b => b.status === 'pending');
  const approvedBonuses = bonuses.filter(b => b.status === 'approved' || b.status === 'paid');
  const totalPending = pendingBonuses.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const totalApproved = approvedBonuses.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

  const handleAddBonus = async () => {
    if (!formData.userId || !formData.amount) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await createBonus({
        userId: formData.userId,
        type: formData.type,
        amount: Number(formData.amount),
        reason: formData.reason
      });
      toast.success('Bonus created successfully');
      setShowAddModal(false);
      setFormData({ userId: '', type: 'performance', amount: '', reason: '' });
      setModalSearchQuery('');
      setModalDeptFilter('all');
      loadData();
    } catch (error) {
      toast.error('Failed to create bonus');
    }
  };

  // Get unique departments for filter
  const departments = [...new Set(users.map(u => u.profile?.department).filter(Boolean))];

  // Filter users in modal
  const filteredModalUsers = users.filter(u => {
    const matchesSearch = modalSearchQuery === '' ||
      u.profile?.firstName?.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
      u.profile?.lastName?.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(modalSearchQuery.toLowerCase());
    const matchesDept = modalDeptFilter === 'all' || u.profile?.department === modalDeptFilter;
    return matchesSearch && matchesDept;
  });

  const handleApprove = async (id) => {
    try {
      await approveBonus(id, user.id);
      toast.success('Bonus approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve bonus');
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectBonus(id, 'Not approved');
      toast.success('Bonus rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject bonus');
    }
  };

  const getEmployeeName = (userId) => {
    const employee = users.find(u => u.id === userId);
    return employee ? `${employee.profile?.firstName} ${employee.profile?.lastName}` : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bonus Management</h1>
          <p className="text-gray-500 mt-1">Manage employee bonuses and incentives</p>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Bonus
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Bonuses</p>
                <p className="text-2xl font-bold text-gray-900">{bonuses.length}</p>
              </div>
              <PiggyBank className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{pendingBonuses.length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPending)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved Total</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalApproved)}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by employee or reason..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bonuses Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBonuses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No bonuses found
                  </TableCell>
                </TableRow>
              ) : (
                filteredBonuses.map(bonus => {
                  const typeInfo = getBonusTypeInfo(bonus.type);
                  return (
                    <TableRow key={bonus.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                            <Award className="w-4 h-4" />
                          </div>
                          <span className="font-medium">{getEmployeeName(bonus.userId)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-gray-600">
                        {bonus.reason || '-'}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        {formatCurrency(bonus.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getPayrollStatusColor(bonus.status)}>
                          {formatPayrollStatus(bonus.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {formatDate(bonus.createdAt)}
                      </TableCell>
                      <TableCell className="text-center">
                        {bonus.status === 'pending' ? (
                          <div className="flex justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleApprove(bonus.id)}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleReject(bonus.id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Bonus Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) {
          setModalSearchQuery('');
          setModalDeptFilter('all');
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Bonus</DialogTitle>
            <DialogDescription>Create a new bonus for an employee</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Search and Filter for Employee Selection */}
            <div className="space-y-3">
              <Label>Find Employee *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by name or email..."
                    value={modalSearchQuery}
                    onChange={(e) => setModalSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={modalDeptFilter} onValueChange={setModalDeptFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Depts</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Employee Selection List */}
              <div className="border rounded-lg max-h-40 overflow-y-auto">
                {filteredModalUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No employees found
                  </div>
                ) : (
                  filteredModalUsers.map(u => (
                    <div
                      key={u.id}
                      onClick={() => setFormData({ ...formData, userId: u.id })}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 transition-colors ${
                        formData.userId === u.id ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''
                      }`}
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                        {u.profile?.firstName?.[0]}{u.profile?.lastName?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">
                          {u.profile?.firstName} {u.profile?.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{u.profile?.department} • {u.profile?.position}</p>
                      </div>
                      {formData.userId === u.id && (
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      )}
                    </div>
                  ))
                )}
              </div>
              {formData.userId && (
                <p className="text-xs text-teal-600">
                  Selected: {users.find(u => u.id === formData.userId)?.profile?.firstName} {users.find(u => u.id === formData.userId)?.profile?.lastName}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Bonus Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="spot">Spot Award</SelectItem>
                    <SelectItem value="signing">Signing</SelectItem>
                    <SelectItem value="retention">Retention</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="pl-10"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Describe the reason for this bonus..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAddBonus}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
            >
              Create Bonus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
